import React, { useEffect, useState } from "react";
import {
  Form,
  Loading,
  ButtonSet,
  Tabs,
  Tab,
  Button,
  TextInput,
  TextArea,
  Row,
  Column,
  FormGroup,
  FileUploader,
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  InlineNotification,
} from "carbon-components-react";
import {
  ArrowRight20,
  ArrowLeft20,
  TrashCan32,
  Edit24,
  SendAlt24,
  Undo24,
} from "@carbon/icons-react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import Card from "../../components/Card/Card";
import SubmitButton from "../../components/Buttons/SubmitButton";
import CancelButton from "../../components/Buttons/CancelButton";
import http from "../../http-common";
import { prototypeSchema } from "../../services/SchemaService";
import { getDocumentTypeDescription } from "../../services/Utils";

const PROTOTYPE_DOC_TYPES = [
  {
    type: "USER_MANUAL",
    label: "Manual do Utilizador",
    description: "Carregue o manual do utilizador da plataforma. (formato PDF)",
    index: "1",
  },
  {
    type: "TECHNICAL_DOCUMENTATION",
    label: "Documentação Técnica",
    description: "Carregue a documentação técnica do sistema. (formato PDF)",
    index: "2",
  },
  {
    type: "PRESENTATION",
    label: "Apresentação",
    description: "Carregue a apresentação do protótipo. (formato PDF)",
    index: "3",
  },
  {
    type: "COMPLEMENTARY_MATERIAL",
    label: "Material Complementar",
    description: "Carregue materiais complementares relevantes. (formato PDF)",
    index: "4",
  },
];

const APPLICATION_SUBMITTED_STATUS_CODE = 2;

const CreatePrototypePage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [prototype, setPrototype] = useState(null);
  const [application, setApplication] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitLocking, setIsSubmitLocking] = useState(false);
  const [fileStatus, setFileStatus] = useState({
    USER_MANUAL: "edit",
    TECHNICAL_DOCUMENTATION: "edit",
    PRESENTATION: "edit",
    COMPLEMENTARY_MATERIAL: "edit",
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(prototypeSchema),
    mode: "onBlur",
  });

  const isSubmitted = prototype?.prototypeStatus === "SUBMITTED";
  const isDraft = prototype?.prototypeStatus === "DRAFT";

  useEffect(() => {
    loadPageData();
  }, []);

  const loadPageData = async () => {
    await Promise.all([loadApplication(), loadMyPrototype()]);
  };

  const loadApplication = async () => {
    try {
      const response = await http.get("/applications/load-user-application");
      setApplication(response.data);
    } catch {
      // no application found for this user
    }
  };

  const loadMyPrototype = async () => {
    try {
      const response = await http.get("/prototypes/my");
      const data = response.data;
      setPrototype(data);
      reset({
        platformUrl: data.platformUrl,
        credentialUsername: data.credentialUsername,
        credentialPassword: data.credentialPassword,
        functionalityDescription: data.functionalityDescription,
        additionalInfo: data.additionalInfo,
      });
      if (data.documents && data.documents.length > 0) {
        const formattedDocs = data.documents.map((doc) => ({
          id: doc.id,
          fileName: doc.fileName,
          fileType: doc.fileType,
          documentType: doc.documentType,
          size: doc.size,
          existingFile: true,
        }));
        setDocuments(formattedDocs);
        const newFileStatus = { ...fileStatus };
        formattedDocs.forEach((doc) => {
          newFileStatus[doc.documentType] = "complete";
        });
        setFileStatus(newFileStatus);
      }
    } catch (error) {
      const status = error?.response?.status;
      if (status !== 404 && status !== 400) {
        toast.error("Erro ao carregar protótipo");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        resolve(reader.result.split(",")[1]);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFileChange = (documentType) => async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Arquivo excede o limite de 10MB");
      setFileStatus((prev) => ({ ...prev, [documentType]: "edit" }));
      return;
    }

    if (!file.type.includes("pdf")) {
      toast.error("Apenas arquivos PDF são permitidos");
      setFileStatus((prev) => ({ ...prev, [documentType]: "edit" }));
      return;
    }

    try {
      const base64Content = await fileToBase64(file);
      const newDocument = {
        fileName: file.name,
        fileContent: base64Content,
        fileType: file.type,
        documentType,
        size: file.size,
      };
      setDocuments((prev) => [
        ...prev.filter((doc) => doc.documentType !== documentType),
        newDocument,
      ]);
      setFileStatus((prev) => ({ ...prev, [documentType]: "complete" }));
      toast.success("Arquivo carregado com sucesso");
    } catch {
      toast.error("Erro ao processar arquivo");
    }
  };

  const handleRemoveDocument = async (documentType) => {
    const docToRemove = documents.find(
      (doc) => doc.documentType === documentType,
    );
    if (!docToRemove) return;

    try {
      if (docToRemove.id) {
        await http.delete(`/prototypes/documents/${docToRemove.id}`);
      }
      setDocuments((prev) =>
        prev.filter((doc) => doc.documentType !== documentType),
      );
      setFileStatus((prev) => ({ ...prev, [documentType]: "edit" }));
      toast.success("Documento removido com sucesso");
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Erro ao remover documento",
      );
    }
  };

  const getExistingDocument = (documentType) =>
    documents.find((doc) => doc.documentType === documentType);

  const onSave = async (data) => {
    try {
      setIsSubmitting(true);
      const newDocuments = documents.filter((doc) => !doc.existingFile);

      if (!prototype) {
        const payload = {
          ...data,
          applicationUuid: application?.uuid,
          documents: newDocuments,
        };
        await http.post("/prototypes", payload);
        toast.success("Protótipo guardado com sucesso");
      } else {
        const payload = {
          ...data,
          uuid: prototype.uuid,
          documents: newDocuments,
        };
        await http.put("/prototypes", payload);
        toast.success("Protótipo actualizado com sucesso");
      }
      await loadMyPrototype();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Erro ao guardar protótipo",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmitPrototype = async () => {
    if (
      !window.confirm(
        "Tem a certeza que deseja submeter o protótipo? Esta acção é irreversível.",
      )
    ) {
      return;
    }
    try {
      setIsSubmitLocking(true);
      await http.put("/prototypes/submit", { uuid: prototype.uuid });
      toast.success("Protótipo submetido com sucesso");
      await loadMyPrototype();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Erro ao submeter protótipo",
      );
    } finally {
      setIsSubmitLocking(false);
    }
  };

  const nextTab = () => setActiveTab((prev) => prev + 1);
  const previousTab = () => setActiveTab((prev) => prev - 1);

  const isApplicationSubmitted =
    application?.currentStatusCode === APPLICATION_SUBMITTED_STATUS_CODE;

  if (!isLoading && !isApplicationSubmitted) {
    return (
      <Card title="O Meu Protótipo">
        <InlineNotification
          kind="warning"
          title="Candidatura não submetida"
          subtitle="Apenas concorrentes com candidatura submetida podem registar ou editar um protótipo. Submeta a sua candidatura primeiro."
          hideCloseButton
          style={{ maxWidth: "100%" }}
        />
      </Card>
    );
  }

  return (
    <Card title="O Meu Protótipo">
      <Loading
        active={isLoading || isSubmitting || isSubmitLocking}
        description="Active loading indicator"
        withOverlay={true}
      />

      {isSubmitted && (
        <InlineNotification
          kind="success"
          title="Protótipo submetido"
          subtitle="O seu protótipo foi submetido com sucesso e não pode ser modificado."
          hideCloseButton
          style={{ marginBottom: "1rem", maxWidth: "100%" }}
        />
      )}

      <Form onSubmit={handleSubmit(onSave)}>
        <Tabs
          scrollIntoView={false}
          selected={activeTab}
          type="container"
          onSelectionChange={(e) => setActiveTab(e)}
        >
          <Tab href="#" id="tab-proto-0" label="Informação Geral">
            <FormGroup
              legendText="DADOS DO PROTÓTIPO"
              className="section-header"
            >
              <fieldset className="bx--fieldset">
                <Row className="mb-4">
                  <Column sm={12} md={12} lg={12}>
                    <TextInput
                      id="platformUrl"
                      labelText="Link de Acesso à Plataforma / Aplicativo *"
                      placeholder="https://..."
                      invalid={!!errors.platformUrl}
                      invalidText={errors.platformUrl?.message}
                      readOnly={isSubmitted}
                      {...register("platformUrl")}
                    />
                  </Column>
                </Row>

                <Row className="mb-4">
                  <Column sm={12} md={6} lg={6}>
                    <TextInput
                      id="credentialUsername"
                      labelText="Utilizador de Acesso *"
                      placeholder="Nome de utilizador / email"
                      invalid={!!errors.credentialUsername}
                      invalidText={errors.credentialUsername?.message}
                      readOnly={isSubmitted}
                      {...register("credentialUsername")}
                    />
                  </Column>
                  <Column sm={12} md={6} lg={6}>
                    <TextInput
                      id="credentialPassword"
                      labelText="Senha de Acesso *"
                      placeholder="Senha ou indicação de acesso demonstrativo"
                      invalid={!!errors.credentialPassword}
                      invalidText={errors.credentialPassword?.message}
                      readOnly={isSubmitted}
                      {...register("credentialPassword")}
                    />
                  </Column>
                </Row>

                <Row className="mb-4">
                  <Column sm={12} md={12} lg={12}>
                    <TextArea
                      id="functionalityDescription"
                      labelText="Descrição das Principais Funcionalidades *"
                      placeholder="Descreva as principais funcionalidades do aplicativo..."
                      rows={5}
                      invalid={!!errors.functionalityDescription}
                      invalidText={errors.functionalityDescription?.message}
                      readOnly={isSubmitted}
                      {...register("functionalityDescription")}
                    />
                  </Column>
                </Row>

                <Row>
                  <Column sm={12} md={12} lg={12}>
                    <TextArea
                      id="additionalInfo"
                      labelText="Informações Adicionais"
                      placeholder="Informações adicionais relevantes para avaliação técnica, operacional e estratégica..."
                      rows={4}
                      readOnly={isSubmitted}
                      {...register("additionalInfo")}
                    />
                  </Column>
                </Row>
              </fieldset>
            </FormGroup>

            <div className="tab-navigation">
              <p></p>
              <Button kind="ghost" size="sm" onClick={nextTab}>
                Próximo <ArrowRight20 />
              </Button>
            </div>
          </Tab>

          <Tab href="#" id="tab-proto-1" label="Documentos">
            <FormGroup
              legendText="DOCUMENTOS DO PROTÓTIPO"
              className="section-header"
            >
              <fieldset className="bx--fieldset">
                {PROTOTYPE_DOC_TYPES.map(
                  ({ type, label, description, index }) => (
                    <Row key={type} className="mb-4">
                      <Column sm={12} md={12} lg={12}>
                        {isSubmitted ? (
                          <p>
                            <strong>
                              {index}. {label}:
                            </strong>{" "}
                            {getExistingDocument(type)?.fileName ||
                              "Não submetido"}
                          </p>
                        ) : (
                          <FileUploader
                            id={`uploader-${type}`}
                            labelTitle={`${index}. ${label}`}
                            labelDescription={description}
                            buttonLabel="Carregar Arquivo"
                            accept={[".pdf"]}
                            multiple={false}
                            filenameStatus={fileStatus[type]}
                            onChange={handleFileChange(type)}
                            onDelete={() => handleRemoveDocument(type)}
                            value={getExistingDocument(type)?.fileName || ""}
                          />
                        )}
                      </Column>
                    </Row>
                  ),
                )}

                {documents.length > 0 && (
                  <DataTable
                    rows={documents.map((doc) => ({
                      ...doc,
                      id: doc.documentType,
                    }))}
                    headers={[
                      { key: "fileName", header: "Nome do Ficheiro" },
                      { key: "documentType", header: "Tipo de Documento" },
                      { key: "actions", header: "Ações" },
                    ]}
                  >
                    {({ rows, headers, getHeaderProps, getTableProps }) => (
                      <Table {...getTableProps()}>
                        <TableHead>
                          <TableRow>
                            {headers.map((header) => (
                              <TableHeader
                                {...getHeaderProps({ header })}
                                key={header.key}
                              >
                                {header.header}
                              </TableHeader>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {documents.map((doc) => (
                            <TableRow key={doc.documentType}>
                              <TableCell>{doc.fileName}</TableCell>
                              <TableCell>
                                {getDocumentTypeDescription(doc.documentType)}
                              </TableCell>
                              <TableCell>
                                {!isSubmitted && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRemoveDocument(doc.documentType)
                                    }
                                    className="bx--btn bx--btn--ghost bx--btn--sm"
                                  >
                                    <TrashCan32 />
                                  </button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </DataTable>
                )}
              </fieldset>
            </FormGroup>

            <div className="tab-navigation">
              <Button kind="ghost" size="sm" onClick={previousTab}>
                <ArrowLeft20 /> Anterior
              </Button>
            </div>
          </Tab>
        </Tabs>

        {!isSubmitted && (
          <div className="form-buttons">
            <ButtonSet>
              <CancelButton icon={Undo24} label="Voltar" />
              <SubmitButton className="emprega-bg-green" icon={Edit24} />
            </ButtonSet>
            {isDraft && (
              <ButtonSet>
                <SubmitButton
                  type="button"
                  label="Submeter"
                  icon={SendAlt24}
                  onClick={onSubmitPrototype}
                />
              </ButtonSet>
            )}
          </div>
        )}
      </Form>
    </Card>
  );
};

export default CreatePrototypePage;
